/**
 * Utility functions to handle ResizeObserver errors
 * These errors are common in React applications with rapid DOM changes
 */

/**
 * Suppress ResizeObserver errors globally
 * Call this once in your app initialization
 */
export const suppressResizeObserverErrors = () => {
  // Handle ResizeObserver errors more comprehensively
  const handleError = (event) => {
    if (event.message && (
      event.message.includes("ResizeObserver") ||
      event.message.includes("ResizeObserver loop completed with undelivered notifications") ||
      event.message.includes("ResizeObserver loop limit exceeded")
    )) {
      event.stopImmediatePropagation()
      event.preventDefault()
      return true
    }
  }

  // Handle both error event and unhandledrejection
  window.addEventListener("error", handleError)
  window.addEventListener("unhandledrejection", (event) => {
    if (event.reason && event.reason.message && event.reason.message.includes("ResizeObserver")) {
      event.preventDefault()
    }
  })

  // Override console.error temporarily to suppress ResizeObserver errors
  const originalConsoleError = console.error
  console.error = (...args) => {
    const message = args.join(' ')
    if (message.includes('ResizeObserver')) {
      // Suppress ResizeObserver errors from console
      return
    }
    originalConsoleError.apply(console, args)
  }

  return () => {
    window.removeEventListener("error", handleError)
    console.error = originalConsoleError
  }
}

/**
 * Debounce function for state updates to prevent rapid DOM changes
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, delay = 16) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

/**
 * Custom hook to handle ResizeObserver errors in components
 * @returns {void}
 */
export const useResizeObserverErrorSuppression = () => {
  if (typeof window !== 'undefined') {
    const cleanup = suppressResizeObserverErrors()
    
    // Return cleanup function for useEffect
    return cleanup
  }
  return () => {}
}
